import React, { useState } from 'react';
import styled from 'styled-components';
import { X } from 'lucide-react';

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const Label = styled.label`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${(p) => p.theme.textSecondary};

  .req {
    color: ${(p) => p.theme.error};
    margin-left: 0.15rem;
  }
`;

const InputWrapper = styled.div<{ $error?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem;
  width: 100%;
  box-sizing: border-box;
  min-height: 42px;
  padding: 0.4rem 0.6rem;
  background: ${(p) => p.theme.surface};
  border: 1px solid ${(p) => (p.$error ? p.theme.error : p.theme.border)};
  border-radius: ${(p) => p.theme.radius};
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus-within {
    border-color: ${(p) => p.theme.primary};
    box-shadow: 0 0 0 3px ${(p) => p.theme.primary}22;
  }
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.25rem 0.5rem;
  background: ${(p) => p.theme.accent};
  color: ${(p) => p.theme.textInverted};
  border-radius: ${(p) => p.theme.radiusPill};
  font-size: 0.8125rem;
  font-weight: 600;
  white-space: nowrap;
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 0;
  opacity: 0.8;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }

  svg {
    font-size: 1rem;
  }
`;

const StyledInput = styled.input`
  flex: 1;
  min-width: 140px;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.9rem;
  font-family: inherit;
  color: ${(p) => p.theme.textPrimary};

  &::placeholder {
    color: ${(p) => p.theme.textMuted};
  }
`;

const HelpText = styled.small`
  font-size: 0.75rem;
  color: ${(p) => p.theme.textMuted};
`;

const ErrorText = styled.span`
  font-size: 0.75rem;
  color: ${(p) => p.theme.error};
`;

interface TagsInputProps {
  label?: string;
  value: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  error?: string;
  helpText?: string;
  required?: boolean;
  containerStyle?: React.CSSProperties;
}

/**
 * Freeform tag/chip input: digite um valor e pressione Enter ou vírgula para
 * adicioná-lo como chip; clique no X (ou Backspace com o campo vazio) para
 * remover. Usado para listas curtas de texto livre (ex.: "Recursos" de um
 * plano) - visual de chip igual ao dos multi-selects de catálogo fixo.
 */
export const TagsInput: React.FC<TagsInputProps> = ({
  label,
  value,
  onChange,
  placeholder = 'Digite e pressione Enter...',
  error,
  helpText,
  required,
  containerStyle,
}) => {
  const [inputValue, setInputValue] = useState('');

  const addTag = (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed || value.includes(trimmed)) return;
    onChange([...value, trimmed]);
    setInputValue('');
  };

  const removeTag = (tag: string) => onChange(value.filter((t) => t !== tag));

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(inputValue);
    } else if (e.key === 'Backspace' && inputValue === '' && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  return (
    <Group style={containerStyle}>
      {label && (
        <Label>
          {label}
          {required && <span className="req">*</span>}
        </Label>
      )}
      <InputWrapper $error={!!error}>
        {value.map((tag) => (
          <Tag key={tag}>
            {tag}
            <RemoveButton type="button" onClick={() => removeTag(tag)}>
              <X size={14} />
            </RemoveButton>
          </Tag>
        ))}
        <StyledInput
          type="text"
          value={inputValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => addTag(inputValue)}
          placeholder={value.length === 0 ? placeholder : ''}
        />
      </InputWrapper>
      {error ? (
        <ErrorText>{error}</ErrorText>
      ) : (
        helpText && <HelpText>{helpText}</HelpText>
      )}
    </Group>
  );
};

export default TagsInput;
